import { AnyEventObject, assertEvent, ErrorActorEvent } from 'xstate';
import { ActionEventParams, ActorErrorParams } from '../../../apply/lib/types';
import { ApplicationContext } from './applicationMachine';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  EvaluateApplicationErrorResult,
  FetchApplicationResult,
  FetchLatestApplicationResult,
  LocateExchangedOfferAndGenerateDisclosuresResult,
  PrepareInStoreApplicationResult,
  ReCreateApplicationResult,
  ReCreateInStoreContingentApplicationResult,
} from './types/actors';
import {
  ApplicationCheckoutActorDone,
  ApplicationMachineEvents,
  ApplicationPrepareCheckoutActorDone,
  CreateApplicationActorDone,
  FetchApplicationActorDone,
  FetchLatestApplicationActorDone,
  LocateExchangedOfferAndGenerateDisclosuresActorDone,
  PrepareInStoreApplicationActorDone,
  ReCreateApplicationActorDone,
  ReCreateInStoreContingentApplicationActorDone,
  SendAssignGeneratedDisclosures,
  SendAssignGeneratedDisclosuresParams,
  SendAssignSelectedOffer,
  SendAssignSelectedOfferParams,
  SendChildMachineReady,
  SendChildMachineReadyParams,
  SendEvaluateApplicationErrorActorDone,
} from './types/events';

/**
 * Get SendAssignGeneratedDisclosuresParams from SendAssignGeneratedDisclosures event
 * @param ActionEventParams<ApplicationContext, SendAssignGeneratedDisclosures>
 * @returns SendAssignGeneratedDisclosuresParams
 */
export const getAssignGeneratedDisclosuresParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  SendAssignGeneratedDisclosures
>): SendAssignGeneratedDisclosuresParams => {
  assertEvent(event, 'SEND_ASSIGN_GENERATED_DISCLOSURES');

  return event.params;
};

/**
 * Gets SendAssignSelectedOfferParams from SendAssignSelectedOffer event
 * @param ActionEventParams<ApplicationContext, SendAssignSelectedOffer>
 * @returns SendAssignSelectedOfferParams
 */
export const getAssignSelectedOfferParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  SendAssignSelectedOffer
>): SendAssignSelectedOfferParams => {
  assertEvent(event, 'SEND_ASSIGN_SELECTED_OFFER');

  return event.params;
};

/**
 * Get FetchApplicationResult from FetchApplicationActorDone event
 * @param ActionEventParams<ApplicationContext,  FetchApplicationActorDone>
 * @returns FetchApplicationResult
 */
export const getFetchApplicationParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  FetchApplicationActorDone
>): FetchApplicationResult => {
  return event.output;
};

/**
 * Get FetchLatestApplicationResult from FetchLatestApplicationActorDone event
 * @param ActionEventParams<ApplicationContext,  FetchLatestApplicationActorDone>
 * @returns FetchLatestApplicationResult
 */
export const getFetchLatestApplicationParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  FetchLatestApplicationActorDone
>): FetchLatestApplicationResult => {
  return event.output;
};

/**
 * Get CreateApplicationResult from CreateApplicationDone event
 * @param ActionEventParams<ApplicationContext, CreateApplicationDone>
 * @returns CreateApplicationResult
 */
export const getCreateApplicationParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  CreateApplicationActorDone
>): CreateApplicationResult => {
  return event.output;
};

/**
 * Gets ReCreateApplicationResult from ReCreateApplicationActorDone event
 * @param ActionEventParams<ApplicationContext, ReCreateApplicationActorDone>
 * @returns ReCreateApplicationResult
 */
export const getReCreateApplicationParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ReCreateApplicationActorDone
>): ReCreateApplicationResult => {
  return event.output;
};

/**
 * Gets ApplicationCheckoutResult from ApplicationCheckoutActorDone event
 * @param ActionEventParams<ApplicationContext, ApplicationCheckoutActorDone>
 * @returns ApplicationCheckoutResult
 */
export const getApplicationCheckoutParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ApplicationCheckoutActorDone
>): ApplicationCheckoutResult => {
  return event.output;
};

/**
 *
 * @param ActionEventParams<ApplicationContext, ApplicationMachineEvents>
 * @returns ApplicationContext
 */
export const getApplicationCheckoutCompleteDelayParams = ({
  context,
}: ActionEventParams<
  ApplicationContext,
  AnyEventObject
>): ApplicationContext => {
  return context;
};

/**
 * Gets ApplicationPrepareCheckoutResult from ApplicationPrepareCheckoutActorDone event
 * @param ActionEventParams<ApplicationContext, ApplicationPrepareCheckoutActorDone>
 * @returns ApplicationPrepareCheckoutResult
 */
export const getApplicationPrepareCheckoutParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ApplicationPrepareCheckoutActorDone
>): ApplicationPrepareCheckoutResult => {
  return event.output;
};

/**
 * Gets PrepareInStoreApplicationResult from PrepareInStoreApplicationActorDone event
 * @param ActionEventParams<ApplicationContext, PrepareInStoreApplicationActorDone>
 * @returns PrepareInStoreApplicationResult
 */
export const getPrepareInStoreApplicationParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  PrepareInStoreApplicationActorDone
>): PrepareInStoreApplicationResult => {
  return event.output;
};

/**
 * Gets LocateExchangedOfferAndGenerateDisclosuresResult from
 * LocateExchangedOfferAndGenerateDisclosuresActorDone event
 * @param ActionEventParams<ApplicationContext, LocateExchangedOfferAndGenerateDisclosuresActorDone>
 * @returns LocateExchangedOfferAndGenerateDisclosuresResult
 */
export const getLocateExchangedOfferAndGenerateDisclosuresParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  LocateExchangedOfferAndGenerateDisclosuresActorDone
>): LocateExchangedOfferAndGenerateDisclosuresResult => {
  return event.output;
};

/**
 * Gets ReCreateInStoreContingentApplicationResult from
 * ReCreateInStoreContingentApplicationActorDone event
 * @param ActionEventParams<ApplicationContext, ReCreateInStoreContingentApplicationActorDone>
 * @returns ReCreateInStoreContingentApplicationResult
 */
export const getReCreatingInStoreContingentApplication = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ReCreateInStoreContingentApplicationActorDone
>): ReCreateInStoreContingentApplicationResult => {
  return event.output;
};

/**
 * Gets machine ready params from SendChildMachineReady event
 * @param ActionEventParams<ApplicationContext, SendChildMachineReady>
 * @returns SendChildMachineReadyParams
 */
export const getAssignChildMachineReadyParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  SendChildMachineReady
>): SendChildMachineReadyParams => {
  return event.params;
};

/**
 * Gets machine ready params from ApplicationMachineEvents
 * @param ActionEventParams<ApplicationContext, DeferredInitializationEvents>
 * @returns SendChildMachineReadyParams
 */
export const getAssignDeferredInitializationEventParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ApplicationMachineEvents
>): ApplicationMachineEvents => {
  return event;
};

/**
 * Gets ActorErrorParams from error event
 * @param ActionEventParams<ApplicationContext, ErrorActorEvent>
 * @returns ActorErrorParams
 */
export const getServiceErrorParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  ErrorActorEvent
>): ActorErrorParams => {
  const errorOutput = event.error as ActorErrorParams;

  return {
    type: event.type,
    error: errorOutput.error,
    reject: errorOutput.reject,
  };
};

/**
 *
 * @param ActionEventParams<ApplicationContext, SendEvaluateApplicationErrorActorDone>
 * @returns EvaluateApplicationErrorResult
 */
export const getEvaluateApplicationErrorParams = ({
  event,
}: ActionEventParams<
  ApplicationContext,
  SendEvaluateApplicationErrorActorDone
>): EvaluateApplicationErrorResult => {
  return event.output;
};
